import { AccountRecord } from '@trustero/trustero-api-web/lib/account/account_pb'
import { NOTIFICATION_TYPE } from '@trustero/trustero-api-web/lib/notification/notification_pb'
import { ProductUpdatesServicePromiseClient } from '@trustero/trustero-api-web/lib/productupdates/productupdates_grpc_web_pb'
import { GetAppVersionRequest } from '@trustero/trustero-api-web/lib/productupdates/productupdates_pb'
import log from 'loglevel'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useGetRoleAccessConfig } from 'src/app/AppAuth/AppAuth.hooks'
import { useReadonlyAccounts } from 'src/components/async/Account/account.hooks'
import { useSwrGrpc } from 'src/components/async/useSwrGrpc'
import { RoadmapAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useAuthContext, useIsReadOnlyUser } from 'src/context/authContext'
import { DATE_LIMIT_UPDATE_MESSAGE } from 'src/pages/AccountSettings/AccountSettings.constants'
import { useLatestNotification } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { BannerState } from 'src/Utils/globalTypes'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { isNewerVersion, getSemverBumpType } from 'src/Utils/semver'

export interface StatusResponse {
  status: {
    indicator: string
    description: string
  }
  page: {
    url: string
  }
}

const fetchStatus = async (): Promise<StatusResponse> => {
  const res = await fetch('https://status.trustero.com/api/v2/status.json')
  return res.json()
}

const emptyBannerState: BannerState = {
  showBanner: false,
  message: '',
  linkText: '',
  link: '',
  isWarning: false,
}

export const useAppStatusBanner = (isDismissed: boolean): BannerState => {
  const [bannerState, setBannerState] = useState<BannerState>(emptyBannerState)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isDismissedRef = useRef(isDismissed)

  // Update ref when isDismissed changes
  useEffect(() => {
    isDismissedRef.current = isDismissed
  }, [isDismissed])

  const fetchAndSetStatus = useCallback(async () => {
    if (isDismissedRef.current) return

    try {
      const status = await fetchStatus()
      if (status.status.indicator !== 'none') {
        setBannerState({
          showBanner: true,
          message:
            'The application is currently experiencing issues. Please check the status page for more information.',
          linkText: 'Visit Status Page',
          link: status.page.url,
          isWarning: true,
        })
      } else {
        setBannerState(emptyBannerState)
      }
    } catch {
      setBannerState(emptyBannerState)
    }
  }, []) // No dependencies needed since we use ref

  useEffect(() => {
    if (isDismissed) {
      setBannerState(emptyBannerState)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    // Initial fetch
    fetchAndSetStatus()

    // Set up interval
    intervalRef.current = setInterval(fetchAndSetStatus, 60000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isDismissed, fetchAndSetStatus])

  return bannerState
}

export const useUserRoleBanner = (): BannerState => {
  const [bannerState, setBannerState] = useState<BannerState>(emptyBannerState)
  const { bannerConfig: userRoleBannerConfig } = useGetRoleAccessConfig()

  useEffect(() => {
    if (userRoleBannerConfig.showBanner) {
      setBannerState({
        ...emptyBannerState,
        showBanner: true,
        message: userRoleBannerConfig.bannerMessage,
        linkText: userRoleBannerConfig.linkText,
        link: userRoleBannerConfig.link,
        internalLink: userRoleBannerConfig.internalLink,
        isWarning: true,
      })
    } else {
      setBannerState(emptyBannerState)
    }
  }, [userRoleBannerConfig])

  return bannerState
}

export const useAccountPurposeBanner = (): BannerState => {
  const [bannerState, setBannerState] = useState<BannerState>(emptyBannerState)
  const isReadonly = useIsReadOnlyUser()
  const { authCtx } = useAuthContext()
  const { data: readonlyAccounts } = useReadonlyAccounts()

  const showAccountPurposeBanner = useMemo(
    () =>
      !isReadonly &&
      readonlyAccounts
        ?.getAccountsList()
        .map((ele: AccountRecord) => ele.getId())
        .includes(authCtx.accountId),
    [isReadonly, readonlyAccounts, authCtx.accountId],
  )

  useEffect(() => {
    if (showAccountPurposeBanner) {
      setBannerState({
        showBanner: true,
        message:
          'You are currently in a Trustero Demo account. Any changes you make will be visible to all users.',
        isWarning: true,
      })
    } else {
      setBannerState(emptyBannerState)
    }
  }, [showAccountPurposeBanner])

  return bannerState
}

export const useDateLimitUpdateBanner = (): BannerState => {
  const [bannerState, setBannerState] = useState<BannerState>(emptyBannerState)
  const location = useLocation()
  const { data, error } = useLatestNotification({
    message: DATE_LIMIT_UPDATE_MESSAGE,
    type: NOTIFICATION_TYPE.BANNER,
  })

  if (error) {
    log.error('Error fetching notifications', error)
  }

  const showDateLimitBanner = useMemo(
    () =>
      !!data?.getNotification()?.getId() &&
      location.pathname.includes(RoadmapAbsoluteRoutes.INDEX),
    [data, location.pathname],
  )

  useEffect(() => {
    if (showDateLimitBanner) {
      setBannerState({
        showBanner: true,
        message:
          'The date limit was updated recently. This affects what you see in the Compliance Roadmap.',
        link: HUBSPOT.DATE_LIMIT,
        isWarning: true,
      })
    } else {
      setBannerState(emptyBannerState)
    }
  }, [showDateLimitBanner])

  return bannerState
}

const PATCH_THROTTLE_MS = 60 * 60 * 1000 // 1 hour in milliseconds

export const useAppVersionBanner = (): BannerState => {
  const [bannerState, setBannerState] = useState<BannerState>(emptyBannerState)
  const lastPatchShownAt = useRef<number | null>(null)

  const request = useMemo(() => new GetAppVersionRequest(), [])
  const { response } = useSwrGrpc(
    ProductUpdatesServicePromiseClient.prototype.getAppVersion,
    request,
    true,
    { refreshInterval: 5 * 60 * 1000, revalidateOnFocus: false },
  )

  useEffect(() => {
    const serverVersion = response.data?.getProductVersion()?.getVersion()
    const clientVersion = process.env.REACT_APP_SEMVER

    if (
      !serverVersion ||
      !clientVersion ||
      !isNewerVersion(serverVersion, clientVersion)
    ) {
      setBannerState(emptyBannerState)
      return
    }

    const bumpType = getSemverBumpType(clientVersion, serverVersion)

    if (bumpType === 'patch') {
      const now = Date.now()
      if (
        lastPatchShownAt.current !== null &&
        now - lastPatchShownAt.current < PATCH_THROTTLE_MS
      ) {
        return
      }
      lastPatchShownAt.current = now
    }

    setBannerState({
      showBanner: true,
      message: 'A new version of the app is available.',
      linkText: 'Reload',
      onCtaClick: () => window.location.reload(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data])

  return bannerState
}
